// Run and Stop. The script becomes a blob: module, so bare imports resolve
// through the page import map and get the instances the page already uses.

import { runtime } from './runtime.js';

export function createRunner( { els, out, library, models, isReady, editor } ) {

	let session = null;

	function setRunning( state ) {

		// state: 'idle' | 'running' | 'listening'
		els.run.disabled = ! isReady();
		els.stop.disabled = state === 'idle';
		els.root.dataset.run = state;
		els.consoleMeta.textContent = state === 'listening' ? 'listening for events — Stop to detach' : '';

	}

	function errorLine( error, run ) {

		// Frames in the script read "<blob url>:line:column".
		if ( ! run?.url ) return null;
		const stack = String( error?.stack ?? '' );
		const at = stack.indexOf( `${ run.url }:` );
		if ( at === - 1 ) return null;
		const match = stack.slice( at + run.url.length ).match( /^:(\d+):(\d+)/ );
		if ( ! match ) return null;
		return { line: Number( match[ 1 ] ), column: Number( match[ 2 ] ) };

	}

	function markError( error, at ) {

		const { monaco, editor: view } = editor();
		if ( ! monaco || ! view || ! at ) return;
		const model = view.getModel();
		const lineNumber = Math.min( at.line, model.getLineCount() );
		monaco.editor.setModelMarkers( model, 'playground', [ {
			severity: monaco.MarkerSeverity.Error,
			message: error.message,
			startLineNumber: lineNumber,
			startColumn: 1,
			endLineNumber: lineNumber,
			endColumn: model.getLineMaxColumn( lineNumber ),
		} ] );
		view.revealLineInCenterIfOutsideViewport( lineNumber );

	}

	function report( error, run = session ) {

		const at = errorLine( error, run );
		out.error( `${ error?.name ?? 'Error' }: ${ error?.message ?? error }${ at ? `  (line ${ at.line })` : '' }` );
		if ( run?.script === library.active ) markError( error, at );

	}

	runtime.out = { report };

	function clearMarkers() {

		const { monaco, editor: view } = editor();
		if ( monaco && view?.getModel() ) monaco.editor.setModelMarkers( view.getModel(), 'playground', [] );

	}

	function endRun( run ) {

		out.trace.delete( run.url );
		URL.revokeObjectURL( run.url );
		if ( runtime.run === run ) runtime.run = null;

	}

	function stop() {

		if ( ! session ) return;
		const ending = session;
		session = null;
		ending.controller.abort();
		for ( const dispose of ending.disposers ) {

			try {

				dispose();

			} catch { /* already detached */ }

		}

		endRun( ending );
		if ( ending.disposers.length || ending.running ) out.debug( 'Stopped' );
		setRunning( 'idle' );

	}

	async function run() {

		if ( ! isReady() ) return;
		stop();
		clearMarkers();

		// No sourceURL comment: stacks must keep the blob URL, which is how
		// errors map to lines and console calls are traced to the script.
		const script = library.active;
		const current = {
			controller: new AbortController(),
			disposers: [],
			running: true,
			script,
			url: URL.createObjectURL( new Blob( [ library.code() ], { type: 'text/javascript' } ) ),
		};
		session = current;
		setRunning( 'running' );
		out.debug( `Run ${ script.title }` );

		let started = performance.now();
		try {

			// Scripts read the open model, so it is open first.
			if ( models.pending() ) out.debug( `Waiting for ${ models.current().label }…` );
			await models.ensure();
			if ( session !== current ) return;
			started = performance.now();

			runtime.run = current;
			out.trace.add( current.url );
			await import( current.url );
			if ( session !== current ) return;
			out.debug( `Done in ${ Math.round( performance.now() - started ) } ms` );

		} catch ( error ) {

			if ( error?.name === 'AbortError' || session !== current ) return;
			report( error, current );

		} finally {

			current.running = false;
			if ( session === current ) {

				if ( current.disposers.length ) {

					setRunning( 'listening' );

				} else {

					session = null;
					endRun( current );
					setRunning( 'idle' );

				}

			}

		}

	}

	els.run.addEventListener( 'click', run );
	els.stop.addEventListener( 'click', stop );
	document.addEventListener( 'keydown', ( event ) => {

		if ( ( event.ctrlKey || event.metaKey ) && event.key === 'Enter' ) {

			event.preventDefault();
			run();

		} else if ( event.key === 'Escape' && session ) {

			stop();

		}

	} );

	return { run, stop, setRunning };

}
